"use client";

import { useCallback } from "react";
import type { JobItemDto } from "@artha/api-contracts";
import type { JobFiltersState } from "@artha/ui";
import { ANALYTICS_EVENTS } from "./events";
import { useAnalytics } from "./useAnalytics";
import { useAudienceAnalyticsContext } from "./AudienceAnalyticsContext";
import { getLastSeenPulseVariant } from "./pulseVariant";
import {
  getPostHogSessionId,
  recordJobApply,
  recordJobView,
} from "./jobJourney";
import {
  buildAudienceJobFilterProps,
  buildAudienceJobProps,
  buildAudienceJobSearchProps,
  buildAudienceSocialMediaClickProps,
  buildAudienceEarnClickProps,
  buildAudienceVpnRestrictionProps,
  buildAudienceCountryRestrictionProps,
  buildAudienceIncognitoRestrictionProps,
  buildAudienceJobAlertEmailSubmittedProps,
  buildAudienceJobAlertPrefsSubmittedProps,
  buildAudienceJobAlertUnsubscribedProps,
  getChangedFilterKeys,
  getFiltersAppliedCount,
} from "./helpers";

/**
 * Variant from the most recent search response, attached as `pulse_variant`
 * to AUD_JOB_* action events. Omitted when no search response has been seen
 * yet.
 */
const getPulseVariantProps = (): { pulse_variant?: string } => {
  const pulseVariant = getLastSeenPulseVariant();
  return pulseVariant ? { pulse_variant: pulseVariant } : {};
};

/**
 * Hook for tracking audience analytics events
 * Automatically includes the creator's username from context in all events
 * 
 * @param userNameOverride - Optional username to override context value
 */
export function useAudienceAnalytics(userNameOverride?: string) {
  const { trackEvent, posthog } = useAnalytics();
  const { username: contextUsername, creatorId } = useAudienceAnalyticsContext();
  
  // Use override if provided, otherwise fall back to context
  const rawUsername = userNameOverride ?? contextUsername;
  const username =
    rawUsername && rawUsername.startsWith("@") ? rawUsername.slice(1) : rawUsername;
  const trackSocialMediaClick = useCallback(
    (socialMedia: string) => {
      const props = {
        ...buildAudienceSocialMediaClickProps(socialMedia),
        ...(username && { username }),
        ...(creatorId && { portal_id: creatorId }),
      };
      trackEvent(ANALYTICS_EVENTS.AUD_SOCIALMEDIA_CLICK, props);
    },
    [trackEvent, username, creatorId]
  );

  const trackJobView = useCallback(
    (job: JobItemDto) => {
      const sessionId = getPostHogSessionId(posthog);
      const journey = recordJobView(
        typeof window !== "undefined" ? window.localStorage : undefined,
        sessionId,
        job.id,
      );
      trackEvent(ANALYTICS_EVENTS.AUD_JOB_VIEW, {
        ...buildAudienceJobProps(job),
        ...journey,
        ...getPulseVariantProps(),
        ...(username && { username }),
        ...(creatorId && { portal_id: creatorId }),
      });
    },
    [posthog, trackEvent, username, creatorId]
  );

  const trackJobApply = useCallback(
    (job: JobItemDto, isHot?: boolean, clickAttemptId?: string) => {
      const sessionId = getPostHogSessionId(posthog);
      const journey = recordJobApply(
        typeof window !== "undefined" ? window.localStorage : undefined,
        sessionId,
        job.id,
      );
      const props = {
        ...buildAudienceJobProps(job),
        ...journey,
        ...getPulseVariantProps(),
        ...(isHot && { is_hot: true }),
        ...(username && { username }),
        ...(clickAttemptId && { click_attempt_id: clickAttemptId }),
        ...(creatorId && { portal_id: creatorId }),
      };
      trackEvent(ANALYTICS_EVENTS.AUD_JOB_APPLY, props);
    },
    [posthog, trackEvent, username, creatorId]
  );

  const trackJobSearch = useCallback(
    (query: string) => {
      const props = {
        ...buildAudienceJobSearchProps(query),
        ...getPulseVariantProps(),
        ...(username && { username }),
        ...(creatorId && { portal_id: creatorId }),
      };
      trackEvent(ANALYTICS_EVENTS.AUD_JOB_SEARCH, props);
    },
    [trackEvent, username, creatorId]
  );

  const trackJobFilterJob = useCallback(
    (previous: JobFiltersState, next: JobFiltersState) => {
      const filtersChanged = getChangedFilterKeys(previous, next);
      if (filtersChanged.length === 0) return;
      const filtersAppliedCount = getFiltersAppliedCount(next);
      const props = {
        ...buildAudienceJobFilterProps(filtersChanged, filtersAppliedCount),
        ...getPulseVariantProps(),
        ...(username && { username }),
        ...(creatorId && { portal_id: creatorId }),
      };
      trackEvent(ANALYTICS_EVENTS.AUD_JOB_FILTER_JOB, props);
    },
    [trackEvent, username, creatorId]
  );

  const trackAudEarnClick = useCallback(
    (username: string) => {
      const props = {
        ...buildAudienceEarnClickProps(username),
        ...(username && { username }),
        ...(creatorId && { portal_id: creatorId }),
      };
      trackEvent(ANALYTICS_EVENTS.AUD_EARN_CLICK, props);
    },
    [trackEvent, username, creatorId]
  );

  const trackVpnRestrictionShown = useCallback(
    (ipTimezone: string, browserTimezone: string, job?: JobItemDto | null, creator?: any) => {
      const props = {
        ...buildAudienceVpnRestrictionProps(ipTimezone, browserTimezone, job, creator),
        ...(username && { username }),
        ...(creatorId && { portal_id: creatorId }),
      };
      console.debug("[Analytics] trackVpnRestrictionShown", props);
      trackEvent(ANALYTICS_EVENTS.AUD_VPN_RESTRICTION_SHOWN, props);
    },
    [trackEvent, username, creatorId]
  );

  const trackCountryRestrictionShown = useCallback(
    (seekerCountry: string, jobCountry: string, job: JobItemDto, creator?: any) => {
      const props = {
        ...buildAudienceCountryRestrictionProps(seekerCountry, jobCountry, job, creator),
        ...(username && { username }),
        ...(creatorId && { portal_id: creatorId }),
      };
      console.debug("[Analytics] trackCountryRestrictionShown", props);
      trackEvent(ANALYTICS_EVENTS.AUD_COUNTRY_RESTRICTION_SHOWN, props);
    },
    [trackEvent, username, creatorId]
  );

  const trackIncognitoRestrictionShown = useCallback(
    (job?: JobItemDto | null, creator?: any) => {
      const props = {
        ...buildAudienceIncognitoRestrictionProps(job, creator),
        ...(username && { username }),
        ...(creatorId && { portal_id: creatorId }),
      };
      console.debug("[Analytics] trackIncognitoRestrictionShown", props);
      trackEvent(ANALYTICS_EVENTS.AUD_INCOGNITO_RESTRICTION_SHOWN, props);
    },
    [trackEvent, username, creatorId]
  );

  const trackJobAlertEmailSubmitted = useCallback(
    (email: string) => {
      const props = {
        ...buildAudienceJobAlertEmailSubmittedProps(email),
        ...(username && { username }),
        ...(creatorId && { portal_id: creatorId }),
      };
      trackEvent(ANALYTICS_EVENTS.AUD_JOB_ALERT_EMAIL_SUBMITTED, props);
    },
    [trackEvent, username, creatorId]
  );

  const trackJobAlertPrefsSubmitted = useCallback(
    (email: string, frequency: string, experience: string) => {
      const props = {
        ...buildAudienceJobAlertPrefsSubmittedProps(email, frequency, experience),
        ...(username && { username }),
        ...(creatorId && { portal_id: creatorId }),
      };
      trackEvent(ANALYTICS_EVENTS.AUD_JOB_ALERT_PREFS_SUBMITTED, props);
    },
    [trackEvent, username, creatorId]
  );

  const trackJobAlertUnsubscribed = useCallback(
    (
      creatorUsername: string,
      subscriptionExternalId: string,
      remainingCreatorAlerts?: number
    ) => {
      const props = {
        ...buildAudienceJobAlertUnsubscribedProps(
          creatorUsername,
          subscriptionExternalId,
          remainingCreatorAlerts
        ),
        ...(username && { username }),
        ...(creatorId && { portal_id: creatorId }),
      };
      trackEvent(ANALYTICS_EVENTS.AUD_JOB_ALERT_UNSUBSCRIBED, props);
    },
    [trackEvent, username, creatorId]
  );

  return {
    trackSocialMediaClick,
    trackJobView,
    trackJobApply,
    trackJobSearch,
    trackJobFilterJob,
    trackAudEarnClick,
    trackVpnRestrictionShown,
    trackCountryRestrictionShown,
    trackIncognitoRestrictionShown,
    trackJobAlertEmailSubmitted,
    trackJobAlertPrefsSubmitted,
    trackJobAlertUnsubscribed,
  };
}
